import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { MainLayout } from './MainLayout';
import { useApp } from './AppContext';

const taxClasses = [
  '1 (Single)',
  '2 (Single with child)',
  '3 (Married, higher income)',
  '4 (Married, equal income)',
  '5 (Married, lower income)',
  '6 (Second job)',
  '7 (Freelance)'
];

const employmentTypes = [
  'Short-term Employment',
  'Minor Employment',
  'Full-time Employment',
  'Part-time Employment'
];

const religionOptions = [
  'Catholic',
  'Protestant',
  'Islamic',
  'Jewish',
  'Other',
  'No religion'
];

export function FinancialData() {
  const { userProfile, setUserProfile, updateProcessState, navigateWithProcessCheck, navigateDirectly, triggerAutomaticBooking, userType } = useApp();
  const [formData, setFormData] = useState({
    iban: userProfile?.iban || '',
    accountHolder: userProfile?.accountHolder || '',
    taxClass: userProfile?.taxClass || '',
    employmentType: userProfile?.employmentType || '',
    religion: userProfile?.religion || '',
    socialSecurityNumber: userProfile?.socialSecurityNumber || '',
  });

  const [validationError, setValidationError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: boolean}>({});

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: false }));
    }
    
    // Clear validation error if all fields are now valid
    if (validationError) {
      const updatedFormData = { ...formData, [field]: value };
      if (validateForm(updatedFormData)) {
        setValidationError('');
      }
    }
  };

  const validateForm = (data = formData) => {
    const requiredFields = ['iban', 'accountHolder', 'taxClass', 'employmentType', 'religion', 'socialSecurityNumber'];
    return requiredFields.every(field => data[field] && data[field].trim() !== '');
  };

  const highlightEmptyFields = () => {
    const requiredFields = ['iban', 'accountHolder', 'taxClass', 'employmentType', 'religion', 'socialSecurityNumber'];
    const newFieldErrors: {[key: string]: boolean} = {};
    
    requiredFields.forEach(field => {
      if (!formData[field] || formData[field].trim() === '') {
        newFieldErrors[field] = true;
      }
    });
    
    setFieldErrors(newFieldErrors);
  };

  const handleSave = () => {
    if (validateForm()) {
      // Clear errors
      setValidationError('');
      setFieldErrors({});
      
      if (userProfile) {
        setUserProfile({ ...userProfile, ...formData });
      }
      
      // Mark Step 2 complete and navigate to Step 3 (Profile Data)
      updateProcessState({ financialProfileCompleted: true });
      navigateDirectly('profile-data');
    } else {
      setValidationError('Please fill in all required fields');
      highlightEmptyFields();
    }
  };

  const handleBack = () => {
    navigateDirectly('apply');
  };

  return (
    <MainLayout showBack onBack={handleBack} showBottomNav={false}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-2">
          Financial Data
        </h1>
        <p className="text-[var(--ucm-text-medium)]">
          Keep your financial data correct, so we can make a correct payout after you finish the job.
        </p>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
              IBAN
            </label>
            <input
              type="text"
              value={formData.iban}
              onChange={(e) => handleInputChange('iban', e.target.value)}
              className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
                fieldErrors.iban ? 'border-[var(--ucm-error-red)]' : 'border-[var(--ucm-border-light)]'
              }`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
              Account Holder
            </label>
            <input
              type="text"
              value={formData.accountHolder}
              onChange={(e) => handleInputChange('accountHolder', e.target.value)}
              className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
                fieldErrors.accountHolder ? 'border-[var(--ucm-error-red)]' : 'border-[var(--ucm-border-light)]'
              }`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
              Tax Class
            </label>
            <div className="relative">
              <select
                value={formData.taxClass}
                onChange={(e) => handleInputChange('taxClass', e.target.value)}
                className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
                  fieldErrors.taxClass ? 'border-[var(--ucm-error-red)]' : 'border-[var(--ucm-border-light)]'
                }`}
              >
                {taxClasses.map(taxClass => (
                  <option key={taxClass} value={taxClass}>
                    {taxClass}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
              Employment Type
            </label>
            <div className="relative">
              <select
                value={formData.employmentType}
                onChange={(e) => handleInputChange('employmentType', e.target.value)}
                className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
                  fieldErrors.employmentType ? 'border-[var(--ucm-error-red)]' : 'border-[var(--ucm-border-light)]'
                }`}
              >
                {employmentTypes.map(employmentType => (
                  <option key={employmentType} value={employmentType}>
                    {employmentType}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
              Religion
            </label>
            <div className="relative">
              <select
                value={formData.religion}
                onChange={(e) => handleInputChange('religion', e.target.value)}
                className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
                  fieldErrors.religion ? 'border-[var(--ucm-error-red)]' : 'border-[var(--ucm-border-light)]'
                }`}
              >
                {religionOptions.map(religion => (
                  <option key={religion} value={religion}>
                    {religion}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
              Social Security Number
            </label>
            <input
              type="text"
              value={formData.socialSecurityNumber}
              onChange={(e) => handleInputChange('socialSecurityNumber', e.target.value)}
              className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
                fieldErrors.socialSecurityNumber ? 'border-[var(--ucm-error-red)]' : 'border-[var(--ucm-border-light)]'
              }`}
            />
          </div>
        </div>

        {/* Validation Error */}
        {validationError && (
          <div className="bg-[var(--ucm-error-red)] bg-opacity-10 border border-[var(--ucm-error-red)] rounded-xl p-3 mt-6">
            <p className="text-[var(--ucm-error-red)] text-sm text-center">{validationError}</p>
          </div>
        )}

        <div className="mt-8">
          <button
            onClick={handleSave}
            disabled={!validateForm()}
            className={`w-full h-14 font-semibold rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2 ${
              validateForm()
                ? 'bg-[var(--ucm-primary-teal)] text-white hover:bg-[var(--ucm-primary-teal-hover)]'
                : 'bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)] cursor-not-allowed'
            }`}
          >
            Continue
          </button>
        </div>
      </div>
    </MainLayout>
  );
}